import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { Calendar } from 'fullcalendar';
import classicThemePlugin from 'fullcalendar/themes/classic';

// The FullCalendar options this wrapper knows how to pass through. Kept as an
// explicit allow-list (rather than the old ember-fullcalendar addon's giant
// catch-all) since it only needs to cover what the two consuming calendars
// actually use.
const SUPPORTED_OPTIONS = [
  'events',
  'eventDisplay',
  'eventTimeFormat',
  'initialView',
  'headerToolbar',
  'plugins',
  'firstDay',
  'locale',
  'views',
  'dateClick',
  'eventClick',
  'eventDidMount',
  'contentHeight',
  'weekends',
  'googleCalendarApiKey',
];

// FullCalendar takes ownership of the element it is mounted on, rendering its
// own DOM inside it. The template's <div> is deliberately empty -- it has no
// children and no dynamic blocks -- so Glimmer never reconciles nodes the
// Calendar created. That <div> also carries the `.full-calendar` class the
// consuming apps' stylesheets hang the calendar theming off, and which the
// classic component used to contribute via `classNames`.
export default class FullCalendar extends Component {
  // The Calendar instance, created once the element is inserted.
  calendar = null;

  // The raw, unprocessed args the Calendar was last built or updated from.
  // Diffing against these (rather than the derived Calendar options below)
  // matters because deriving `plugins`/`eventDisplay` builds a new array/value
  // on every call -- diffing against the derived output would see a "change"
  // on every single update, even when nothing the consumer passed in changed.
  //
  // Deliberately a plain, untracked field: setupCalendar reads it to build the
  // Calendar, and making it tracked would subscribe that modifier to the
  // writes updateCalendar makes to it.
  lastRawOptions;

  constructor(owner, args) {
    super(owner, args);

    // Snapshot the initial configuration up-front. The setup modifier below
    // must build the Calendar without reading `this.args` inside its own
    // execution frame -- doing so would subscribe it to argument changes and
    // cause Glimmer to tear down and recreate the whole Calendar on every
    // update. Reactive changes are handled by updateCalendar instead.
    this.lastRawOptions = this.getRawOptions();
  }

  // The current raw args, handed to updateCalendar as an argument (rather than
  // read inside it) so that modifier re-runs whenever any supported arg
  // changes. Rebuilt on each access, so updateCalendar can also re-run on an
  // unrelated re-render -- harmless, since it diffs before touching the
  // Calendar, exactly as the classic component's didUpdateAttrs did.
  get rawOptions() {
    return this.getRawOptions();
  }

  getRawOptions() {
    let options = {};
    SUPPORTED_OPTIONS.forEach((name) => {
      // name comes from the hardcoded SUPPORTED_OPTIONS list, not user input.
      // eslint-disable-next-line security/detect-object-injection
      let value = this.args[name];
      if (value !== undefined) {
        // eslint-disable-next-line security/detect-object-injection
        options[name] = value;
      }
    });
    return options;
  }

  // The actual options to hand to FullCalendar: the raw args, plus this
  // wrapper's own defaults layered on top.
  getCalendarOptions(rawOptions) {
    let options = { ...rawOptions };
    options.plugins = [classicThemePlugin, ...(options.plugins || [])];
    // Default to FullCalendar's solid colored-block event style rather than
    // the classic theme's month-view dot style, matching how this wrapper's
    // consumers style their events via per-event `className`/`color`.
    if (options.eventDisplay === undefined) {
      options.eventDisplay = 'block';
    }
    return options;
  }

  // Mounts the Calendar on the rendered element and destroys it on teardown,
  // replacing the classic component's didInsertElement/willDestroyElement
  // pair. Reads only plain, untracked fields, so it runs exactly once per
  // element lifecycle.
  setupCalendar = modifier((element) => {
    let calendar = new Calendar(
      element,
      this.getCalendarOptions(this.lastRawOptions),
    );
    this.calendar = calendar;
    calendar.render();

    return () => {
      calendar.destroy();
      this.calendar = null;
    };
  });

  // Pushes changed args into the live Calendar, replacing didUpdateAttrs. Only
  // the keys that actually changed are forwarded to setOption, since re-setting
  // an unchanged option is not free -- `events`, for instance, makes
  // FullCalendar refetch its event source.
  updateCalendar = modifier((element, [rawOptions]) => {
    if (!this.calendar) {
      return;
    }

    let changedNames = SUPPORTED_OPTIONS.filter(
      // Hardcoded list, not user input.
      // eslint-disable-next-line security/detect-object-injection
      (name) => rawOptions[name] !== this.lastRawOptions[name],
    );
    if (changedNames.length) {
      let calendarOptions = this.getCalendarOptions(rawOptions);
      changedNames.forEach((name) => {
        // eslint-disable-next-line security/detect-object-injection
        this.calendar.setOption(name, calendarOptions[name]);
      });
    }
    this.lastRawOptions = rawOptions;
  });
}
